<template>
  <div class="w-full overflow-hidden" :class="wrapperClasses">
    <!-- Loading overlay -->
    <div v-if="loading" class="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
      <BaseSpinner size="lg" />
    </div>

    <div class="overflow-x-auto">
      <table :class="tableClasses">
        <!-- Header -->
        <thead :class="headerClasses">
          <tr>
            <!-- Checkbox column -->
            <th v-if="selectable" class="w-12 px-4 py-3">
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate="someSelected"
                class="rounded border-gray-300 text-primary focus:ring-primary"
                @change="toggleSelectAll"
              />
            </th>

            <!-- Dynamic columns -->
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500',
                column.sortable ? 'cursor-pointer select-none hover:text-gray-700' : '',
                column.align === 'center' ? 'text-center' : '',
                column.align === 'right' ? 'text-right' : '',
                column.headerClass || ''
              ]"
              :style="column.width ? { width: column.width } : {}"
              @click="column.sortable ? handleSort(column) : null"
            >
              <div class="flex items-center gap-1" :class="column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''">
                <slot :name="`header-${column.key}`" :column="column">
                  {{ column.label }}
                </slot>
                <span v-if="column.sortable && sortBy === column.key" class="ml-1 text-gray-400">
                  <i v-if="sortOrder === 'asc'" class="fas fa-chevron-up text-xs"></i>
                  <i v-else class="fas fa-chevron-down text-xs"></i>
                </span>
              </div>
            </th>

            <!-- Actions column -->
            <th v-if="$slots.actions" class="w-24 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
              Actions
            </th>
          </tr>
        </thead>

        <!-- Body -->
        <tbody :class="bodyClasses">
          <template v-if="data.length > 0">
            <tr
              v-for="(row, rowIndex) in data"
              :key="rowKey ? row[rowKey] : rowIndex"
              :class="[
                rowClasses,
                hoverable ? 'hover:bg-gray-50' : '',
                striped && rowIndex % 2 === 1 ? 'bg-gray-50/50' : '',
                isSelected(row) ? 'bg-primary-50' : '',
                clickable ? 'cursor-pointer' : ''
              ]"
              @click="handleRowClick(row, rowIndex)"
            >
              <!-- Checkbox column -->
              <td v-if="selectable" class="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  :checked="isSelected(row)"
                  class="rounded border-gray-300 text-primary focus:ring-primary"
                  @click.stop
                  @change="toggleSelect(row)"
                />
              </td>

              <!-- Dynamic cells -->
              <td
                v-for="column in columns"
                :key="column.key"
                :class="[
                  'px-4 py-3 text-sm text-gray-700',
                  column.align === 'center' ? 'text-center' : '',
                  column.align === 'right' ? 'text-right' : '',
                  column.cellClass || ''
                ]"
              >
                <slot :name="`cell-${column.key}`" :row="row" :value="getCellValue(row, column)" :index="rowIndex">
                  {{ getCellValue(row, column) }}
                </slot>
              </td>

              <!-- Actions column -->
              <td v-if="$slots.actions" class="px-4 py-3 text-right">
                <slot name="actions" :row="row" :index="rowIndex"></slot>
              </td>
            </tr>
          </template>

          <!-- Empty state -->
          <tr v-else>
            <td :colspan="totalColumns" class="px-4 py-16 text-center">
              <slot name="empty">
                <div class="flex flex-col items-center text-gray-400">
                  <i class="fas fa-inbox mb-3 text-3xl"></i>
                  <p class="text-sm font-medium text-gray-500">{{ emptyText }}</p>
                  <p v-if="emptySubtext" class="text-xs text-gray-400 mt-1">{{ emptySubtext }}</p>
                </div>
              </slot>
            </td>
          </tr>
        </tbody>

        <!-- Footer -->
        <tfoot v-if="$slots.footer">
          <tr>
            <td :colspan="totalColumns">
              <slot name="footer"></slot>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import BaseSpinner from './BaseSpinner.vue'

export default {
  name: 'BaseTable',
  components: {
    BaseSpinner
  },
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (value) => value.every(col => col.key && col.label)
    },
    data: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    striped: {
      type: Boolean,
      default: false
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: String,
      default: null
    },
    sortOrder: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    emptyText: {
      type: String,
      default: 'Aucune donnee disponible'
    },
    emptySubtext: {
      type: String,
      default: null
    }
  },
  emits: ['update:selectedRows', 'sort', 'row-click'],
  computed: {
    totalColumns() {
      let count = this.columns.length
      if (this.selectable) count++
      if (this.$slots.actions) count++
      return count
    },
    wrapperClasses() {
      return [
        'relative',
        this.bordered ? 'border border-gray-200 rounded-xl' : ''
      ]
    },
    tableClasses() {
      return [
        'min-w-full',
        this.compact ? 'table-compact' : ''
      ]
    },
    headerClasses() {
      return 'bg-gray-50/80 border-b border-gray-200'
    },
    bodyClasses() {
      return 'bg-white divide-y divide-gray-100'
    },
    rowClasses() {
      return 'transition-colors duration-150'
    },
    allSelected() {
      return this.data.length > 0 && this.selectedRows.length === this.data.length
    },
    someSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.length < this.data.length
    }
  },
  methods: {
    getCellValue(row, column) {
      if (column.formatter) {
        return column.formatter(row[column.key], row)
      }
      return column.key.split('.').reduce((obj, key) => obj?.[key], row)
    },
    isSelected(row) {
      if (!this.rowKey) {
        return this.selectedRows.includes(row)
      }
      return this.selectedRows.some(selected => selected[this.rowKey] === row[this.rowKey])
    },
    toggleSelect(row) {
      const selected = [...this.selectedRows]
      const index = this.rowKey
        ? selected.findIndex(r => r[this.rowKey] === row[this.rowKey])
        : selected.indexOf(row)

      if (index > -1) {
        selected.splice(index, 1)
      } else {
        selected.push(row)
      }

      this.$emit('update:selectedRows', selected)
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.$emit('update:selectedRows', [])
      } else {
        this.$emit('update:selectedRows', [...this.data])
      }
    },
    handleSort(column) {
      let order = 'asc'
      if (this.sortBy === column.key && this.sortOrder === 'asc') {
        order = 'desc'
      }
      this.$emit('sort', { column: column.key, order })
    },
    handleRowClick(row, index) {
      if (this.clickable) {
        this.$emit('row-click', { row, index })
      }
    }
  }
}
</script>

<style scoped>
.table-compact th,
.table-compact td {
  @apply py-2;
}
</style>
